import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LOAD_PACKAGE, LOAD_PACKAGES_WISHLIST } from "GraphQL/Queries";
import { Button } from "components/buttons";
import ReactCountryFlag from "react-country-flag";
import moment from "moment";
import { ModalType } from "constants/modalType";
import Modal from "components/modal/Modal";
import PackageDetailsCarousel from "components/carousel/PackageDetailsCarousel";
import { PACKAGE_WISHLIST_MUTATION } from "GraphQL/Mutations";
import {
  isInThePast,
  checkDeadlineRequirement,
  subtractDaysAndKeepISOFormat,
} from "utils/helpers";
import { errorToast, successToast, warningToast } from "components/toast/toast";
import { BsChevronLeft } from "react-icons/bs";
import { ReactComponent as Like } from "assets/icons/like-fill.svg";
import { ReactComponent as Unlike } from "assets/icons/like-line.svg";
import { GoCheck, GoClock, GoLocation, GoX } from "react-icons/go";
import { Timer } from "@material-ui/icons";
import { ReactComponent as InfoIcon2 } from "assets/icons/info2.svg";
import { ReactComponent as Tag } from "assets/icons/tag.svg";
import { ReactComponent as Share } from "assets/img/share.svg";
import Loader from "components/loader/Loader";
import Checkout from "./Checkout";
import ItineraryTimeline from "./ItineraryTimeline";

import vaultStore from "stores/vaultStore";

const PackageCardDetails = observer(() => {
  const { id } = useParams();
  const userToken = sessionStorage.getItem("token");

  const [packagesWishlistData, setPackagesWishlistData] = useState([]);
  const [inWishlist, setInWishlist] = useState(false);

  const [details, setDetails] = useState({
    url: "",
    code: "",
    title: "",
    description: "",
  });

  const { modalType, setModalType, showModal, setShowModal } = vaultStore;

  const { loading, data } = useQuery(LOAD_PACKAGE, {
    variables: {
      packageId: id,
    },
  });

  const packageDetails = useMemo(() => {
    return data?.getPackage || {};
  }, [data?.getPackage]);

  const images = useMemo(() => {
    return packageDetails?.images || [];
  }, [packageDetails?.images]);

  const wishlistPackage = useQuery(LOAD_PACKAGES_WISHLIST);

  const [activeTab, setActiveTab] = useState("overview");

  const {
    name,
    summary,
    description,
    destination_address,
    booking_deadline_date,
    number_adults,
    room_rates,
    categories,
    country,
    duration_type,
    minimum_travellers,
    inclusions,
    exclusions,
    itineraries,
    start_date,
    end_date,
    vacation_duration,
    deadline_days_from_departure,
    min_days_before_expiry,
    return_date,
    departure_date,
  } = packageDetails;

  const navigate = useNavigate();

  const [addFavourite] = useMutation(PACKAGE_WISHLIST_MUTATION);

  const handleWishlist = (item) => {
    addFavourite({
      variables: {
        package: item.id,
      },
    })
      .then((response) => {
        if (response.data.addFavourite.__typename === "BadRequest") {
          warningToast("Warning", response.data.addFavourite.message);
        }
        if (response.data.addFavourite.__typename === "AddFavouriteSuccess") {
          response.data.addFavourite.favourite
            ? successToast("Success", "Package added to wishlist")
            : successToast("Success", "Package removed from wishlist");
        }
      })
      .catch((error) => {
        errorToast("Error", error ? error?.message : "An error occured");
      });
  };

  useEffect(() => {
    if (wishlistPackage.data) {
      const result = wishlistPackage.data.me.user.favourites;
      const results = [];

      result.forEach((element) => {
        if (element.package !== null) {
          results.push(element.package.id);
        }
      });

      setPackagesWishlistData(results);
    }
  }, [wishlistPackage.data]);

  useEffect(() => {
    if (packagesWishlistData.includes(id)) {
      setInWishlist(true);
    } else {
      setInWishlist(false);
    }
  }, [packagesWishlistData]);

  const [openCheckout, setOpenCheckout] = useState(false);

  const handleShareLink = () => {
    setModalType(ModalType.SOCIAL_MEDIA);
    setDetails({
      ...details,
      url: window.location.href,
      title: name,
      description: summary,
    });
    setShowModal(true);
  };

  const handleOpenCheckout = () => {
    if (!userToken) {
      navigate("/auth/login");
      return;
    } else {
      setOpenCheckout(true);
    }
  };

  const handleCloseCheckout = () => {
    setOpenCheckout(false);
  };

  const findBestPackagePrice = (room_rates) => {
    if (!room_rates || room_rates.length === 0) return null;

    // Filter for all DOUBLE room rates first
    const doubleRoomRates = room_rates.filter(
      (rate) => rate.room_type === "DOUBLE",
    );

    if (doubleRoomRates.length > 0) {
      // Sort the rates by package class priority (BUDGET first, then MID_RANGE, then LUXURY)
      const tierPriority = { BUDGET: 1, MID_RANGE: 2, LUXURY: 3 };

      // Sort by tier priority, then by price as a secondary factor
      const sortedRates = doubleRoomRates.sort((a, b) => {
        // First sort by tier priority
        const tierDiff =
          tierPriority[a.package_class] - tierPriority[b.package_class];
        if (tierDiff !== 0) return tierDiff;

        // If same tier, sort by price
        return parseFloat(a.rate) - parseFloat(b.rate);
      });

      // Return the rate from the lowest tier (or if same tier, the lowest price)
      return sortedRates[0].rate;
    }

    const singleRoomRates = room_rates.filter(
      (rate) => rate.room_type === "SINGLE",
    );

    if (singleRoomRates.length > 0) {
      const tierPriority = { BUDGET: 1, MID_RANGE: 2, LUXURY: 3 };
      const sortedSingleRates = singleRoomRates.sort((a, b) => {
        const tierDiff =
          tierPriority[a.package_class] - tierPriority[b.package_class];
        if (tierDiff !== 0) return tierDiff;
        return parseFloat(a.rate) - parseFloat(b.rate);
      });

      return sortedSingleRates[0].rate;
    }

    // If still nothing found, return null
    return null;
  };

  const price = findBestPackagePrice(packageDetails?.room_rates);

  return (
    <div className="relative">
      {loading && <Loader />}
      {!loading && (
        <div className="w-[100%] space-y-6 ">
          <Modal
            showModal={showModal}
            modalType={modalType}
            setModalType={setModalType}
            setShowModal={() => setShowModal()}
            details={details}
            setDetails={setDetails}
          />
          <div className="  flex justify-between items-center">
            <Link
              to=""
              onClick={() => navigate(-1)}
              className="hover:bg-gray-30 font-medium text-[#667085] text-[18px] cursor-pointer flex items-center gap-x-2"
            >
              <BsChevronLeft size={18} fill="#667085" />
              <p>Go back</p>
            </Link>

            <p
              onClick={() => handleShareLink()}
              className="p-[10px] rounded-full bg-[#F5F7F9] cursor-pointer"
            >
              <Share />
            </p>
          </div>

          {loading && images?.length < 1 && <div>Images Loading...</div>}

          <div className="pb-2 relative">
            <PackageDetailsCarousel
              minTravellers={minimum_travellers}
              images={images}
            />
          </div>

          <div className="flex flex-col gap-2 ">
            <div className=" flex items-center justify-between capitalize ">
              <h3 className=" text-blue-80 font-bold flex items-center gap-2 capitalize text-xl md:text-2xl ">
                {name}
                {<ReactCountryFlag countryCode={country} svg />}
              </h3>
            </div>
            <div className="flex items-center gap-x-4">
              <div className="flex items-center gap-x-1">
                <GoLocation />

                <span className="text-sm text-gray-nav font-bold ">
                  {destination_address}
                </span>
              </div>

              <div className=" border-x-2 border-[#D0D5DD] px-4 flex items-center gap-x-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_9414_68689)">
                    <path
                      d="M2 22C2 19.8783 2.84285 17.8434 4.34315 16.3431C5.84344 14.8429 7.87827 14 10 14C12.1217 14 14.1566 14.8429 15.6569 16.3431C17.1571 17.8434 18 19.8783 18 22H16C16 20.4087 15.3679 18.8826 14.2426 17.7574C13.1174 16.6321 11.5913 16 10 16C8.4087 16 6.88258 16.6321 5.75736 17.7574C4.63214 18.8826 4 20.4087 4 22H2ZM10 13C6.685 13 4 10.315 4 7C4 3.685 6.685 1 10 1C13.315 1 16 3.685 16 7C16 10.315 13.315 13 10 13ZM10 11C12.21 11 14 9.21 14 7C14 4.79 12.21 3 10 3C7.79 3 6 4.79 6 7C6 9.21 7.79 11 10 11ZM18.284 14.703C19.6893 15.3359 20.882 16.3612 21.7186 17.6557C22.5552 18.9502 23.0002 20.4587 23 22H21C21.0002 20.844 20.6666 19.7125 20.0391 18.7416C19.4116 17.7707 18.5171 17.0017 17.463 16.527L18.283 14.703H18.284ZM17.596 3.413C18.6035 3.8283 19.465 4.53354 20.071 5.43923C20.6771 6.34492 21.0004 7.41024 21 8.5C21.0004 9.87233 20.4877 11.1952 19.5625 12.2088C18.6374 13.2224 17.3667 13.8535 16 13.978V11.965C16.7409 11.8589 17.4283 11.518 17.9613 10.9925C18.4943 10.4669 18.8447 9.78432 18.9612 9.04493C19.0776 8.30555 18.954 7.5483 18.6084 6.88435C18.2628 6.22041 17.7134 5.68475 17.041 5.356L17.596 3.413Z"
                      fill="#1C9FDA"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_9414_68689">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-sm font-medium text-gray-nav">
                  {number_adults}
                </span>
              </div>

              <div className="flex items-center gap-x-2">
                <GoClock className="text-[#F6B51E]" />

                <span className="text-sm font-medium text-gray-nav">
                  {vacation_duration}-Day travel experience
                </span>
              </div>
            </div>
            <div className="flex flex-wrap  text-base uppercase lg:text-lg text-[#093549] font-bold">
              {moment(departure_date)?.format("dddd, ll")} -{" "}
              {moment(return_date)?.format("dddd, ll")}
            </div>

            <section className="mt-3 ">
              {/* Tabs */}
              <div className="flex border-b mb-6 ">
                <button
                  className={`border-b-2 flex-1 px-6 py-3  text-base font-medium ${
                    activeTab === "overview"
                      ? "text-gray-700  border-blue-100"
                      : "text-gray-600 border-[#E1E4EA]"
                  }`}
                  onClick={() => setActiveTab("overview")}
                >
                  Overview
                </button>
                <button
                  className={`border-b-2 text-base flex-1 px-6 py-3 font-medium ${
                    activeTab === "itineraries"
                      ? "text-gray-700 border-blue-100"
                      : "text-gray-600 border-[#E1E4EA]"
                  }`}
                  onClick={() => setActiveTab("itineraries")}
                >
                  Itineraries
                </button>
              </div>

              {/* Content */}
              {activeTab === "overview" && (
                <>
                  <div className="">
                    {/* <p className="text-gray-700 text-base ">{summary}</p> */}
                    <p className="text-gray-700 text-base ">{description}</p>

                    <div className="mt-6 border-t border-b border-black-100/10 py-4">
                      <h3 className="text-base lg:text-lg font-bold mb-4">
                        What's Included
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ul className="space-y-4">
                          {inclusions?.map((i, index) => (
                            <li
                              key={index}
                              className="flex items-center gap-x-2"
                            >
                              <GoCheck className="text-black-100" />
                              <span className="capitalize text-base font-bold text-[#344054]">
                                {i}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <ul className="space-y-4">
                          {exclusions?.map((i, index) => (
                            <li
                              key={index}
                              className="flex items-center gap-x-2"
                            >
                              <GoX className="text-black-100" />{" "}
                              <span className="capitalize  text-base font-bold text-[#344054]">
                                {i}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="py-4 border-b border-black-100/10 ">
                      <h3 className="flex items-center gap-x-2 text-sm">
                        <Timer className="text-blue-100" /> Package Expiry Date
                      </h3>

                      {duration_type === "LONG_TERM" &&
                      isInThePast(return_date) ? (
                        <div>
                          <p className="text-sm">
                            Booking for this trip ends:{" "}
                            <span className="text-red-100 ml-2">
                              {moment(return_date).format("dddd, MMM D, YYYY")}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm mt-1">
                          Booking for this trip ends{" "}
                          <span className="ml-2 font-medium text-red-100">
                            {booking_deadline_date
                              ? moment(booking_deadline_date).format(
                                  "dddd, MMM D, YYYY",
                                )
                              : "N/A"}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="py-4">
                      <h3 className="flex items-center gap-2 text-sm  mb-1">
                        <Tag /> Package Tags
                      </h3>
                      <p className="text-gray-700 text-sm flex  flex-wrap items-center gap-x-2">
                        {categories?.map((i) => (
                          <span key={i.categoryId.id}>
                            {i.categoryId?.name},
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "itineraries" && (
                <ItineraryTimeline itineraries={itineraries} />
              )}
            </section>
          </div>
        </div>
      )}

      {openCheckout && (
        <Checkout
          _package={packageDetails}
          close={handleCloseCheckout}
          view={openCheckout}
        />
      )}

      <section className="fixed bottom-0 z-[999] left-0 right-0 w-full bg-[#EDF8FC] px-4 lg:px-0 py-4">
        <article className=" flex flex-col gap-y-4 lg:flex-row lg:items-center justify-between lg:container mx-auto">
          <div>
            <h4 className="text-[#0E4F6D] font-bold text-lg lg:text-2xl">
              <span className="font-medium text-sm mr-2">From</span>${price}
            </h4>

            <div className="flex items-center gap-x-2">
              <span className="font-medium text-sm text-gray-500 underline">
                Best Price Offer
              </span>

              <InfoIcon2 />
            </div>
          </div>

          <div className="flex items-center gap-x-4 lg:gap-x-12">
            <div className="w-fit">
              <Button
                disabled={
                  duration_type === "LONG_TERM"
                    ? isInThePast(return_date)
                    : isInThePast(booking_deadline_date)
                }
                isBlue
                text="Checkout package"
                onClick={handleOpenCheckout}
                className={
                  "w-fit text-sm sm:text-base flex items-center justify-center flex-row-reverse px-2 gap-1  "
                }
              />
            </div>

            <div className="transition cursor-pointer ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300">
              {inWishlist ? (
                <div
                  className="h-9 w-9 rounded-full bg-white-100 flex items-center justify-center"
                  onClick={() => {
                    setInWishlist(false);
                    handleWishlist(packageDetails);
                  }}
                >
                  <Like className="h-6 w-6" />
                </div>
              ) : (
                <div
                  className="h-9 w-9 rounded-full bg-white-100 shadow-sm  flex items-center justify-center"
                  onClick={() => {
                    setInWishlist(true);
                    handleWishlist(packageDetails);
                  }}
                >
                  <Unlike className="h-6 w-6" />
                </div>
              )}
            </div>
          </div>
        </article>
      </section>
    </div>
  );
});

export default PackageCardDetails;
